import { MessageFailedIcon } from '@carboncopyco/icons';
import { CloneNewIllustration } from '@carboncopyco/illustrations';
import {
  Button,
  ColorHex,
  TextBase,
  TextPageHeader,
  TextSubHeader,
  notify,
} from '@carboncopyco/ui';
import { isURLBlockedByDemostack } from '@carboncopyco/utils';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  isURLAllowedByOrg,
  userCanRecordWebsite,
  userHasRecordingPermission,
} from '../../../shared/permissions';
import { isURL, openLinkInBrowser, replaceUrl } from '../../../util/path-util';
import { useUser } from '../../store/context';
import TypeURLInput from '../TypeURLInput';

interface NewCloneBodyProps {
  onNewClone: (url: string) => void;
}

export const NewCloneBody = ({ onNewClone }: NewCloneBodyProps) => {
  const { me, activeOrganization } = useUser();
  const [url, setUrl] = useState<string>('');
  const [isLoadingStartRecording, setIsLoadingStartRecording] = useState(false);
  const [isURLBlocked, setIsURLBlocked] = useState<boolean>(true);
  const [debouncedUrl] = useDebounce(url, 500);

  const hasRecordingPermission = userHasRecordingPermission(
    me,
    activeOrganization.id
  );
  const isRecordingAllowed = userCanRecordWebsite(
    debouncedUrl,
    activeOrganization
  );

  const getIsAlert = () => {
    return !isRecordingAllowed;
  };

  const handleStartNewRecording = () => {
    setIsLoadingStartRecording(true);

    if (isURLBlocked) {
      notify.error(
        'The website you’re recording cannot be served by Demostack.'
      );
      setIsLoadingStartRecording(false);
      return;
    }

    const isURLAllowed = isURLAllowedByOrg(url, activeOrganization);
    if (!isURLAllowed) {
      notify.error(
        'Please use a URL of an approved app or demo environment. Contact your account manager to record more apps.'
      );
      setIsLoadingStartRecording(false);
      return;
    }

    if (!isURL(url)) {
      notify.error('Oops, seems like the URL you typed does not exist');
      setIsLoadingStartRecording(false);
      return;
    }

    onNewClone(url);
  };

  const handleKeyUp = () => {
    const newUrl = replaceUrl(url);
    setUrl(newUrl);
  };

  const onUrlChanged = (newUrl: string) => {
    setUrl(newUrl);
  };

  useEffect(() => {
    const isBlocked = isURLBlockedByDemostack(url);
    setIsURLBlocked(isBlocked);
  }, [url]);

  return hasRecordingPermission ? (
    <div className=" bg-ds-accent3-100  flex flex-col justify-center items-center py-10 flex-grow flex-shrink-0 pb-52">
      <CloneNewIllustration
        height={100}
        width={148}
      />
      <div className="pt-10">
        <TextPageHeader label="Start with a clone!" />
      </div>
      <div className="pt-2">
        <TextSubHeader label="Type your product’s URL here:" />
      </div>
      <div className="pt-9 w-full flex flex-col items-center justify-center">
        <div />
        <TypeURLInput
          url={url}
          onKeyUp={handleKeyUp}
          onUrlChanged={onUrlChanged}
          isAlert={getIsAlert()}
          isButtonEnabled={isRecordingAllowed && !isLoadingStartRecording}
          startNewRecording={handleStartNewRecording}
        />
        <div className="flex items-center gap-1.5 pt-5">
          <MessageFailedIcon color={ColorHex.primary} />
          <TextBase
            label="Tips for secured cloning"
            color="text-ds-natural-600"
          />
          <Button
            label="Learn more"
            link
            blue
            onClick={() =>
              openLinkInBrowser(
                null,
                'https://help.demostack.com/en/articles/6202302'
              )
            }
          />
        </div>
      </div>
    </div>
  ) : (
    <div className="flex flex-1 bg-ds-accent3-200 flex-col justify-center items-center pt-32">
      <div className="pt-10" />
      <div className="pt-3">
        <TextBase
          label="Recording is not available for viewers."
          color="text-ds-natural-600"
        />
      </div>
      <TextBase
        label="View permissions in your Demostack’s account."
        color="text-ds-natural-600"
      />
    </div>
  );
};
